import { useEffect, useState } from "react";
import { SHRINE_THEMES } from "./anchorShrine";
import NameSigil from "./nameSigil";
import { fetchFaceWall, type FaceWall } from "../lib/faceRegistry";

// face.jic.io 首页的锚点墙:神明中转站 + 预注册锚点 + 自愿上墙的真人(只有 @名字,不是照片)
type WallAnchor = {
  slug: string;
  label: string;
  kind: "relay" | "prereg";
  templates?: number;
  claimedBy?: string | null;
  bless?: { left: number };
};
type Filter = "all" | "relay" | "prereg" | "people";

function shortDate(iso: string) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : `${date.getMonth() + 1}月${date.getDate()}日`;
}

export default function AnchorWall({ claimedSlugs, myFaceHref }: { claimedSlugs: string[]; myFaceHref: string }) {
  const [anchors, setAnchors] = useState<WallAnchor[] | null>(null);
  const [wall, setWall] = useState<FaceWall | null>(null);
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    let cancelled = false;
    fetch("https://u.jic.io/v1/face/anchors")
      .then((response) => response.json())
      .then((data: { anchors?: WallAnchor[] }) => {
        if (!cancelled) setAnchors(data.anchors || []);
      })
      .catch(() => {
        if (!cancelled) setAnchors([]);
      });
    fetchFaceWall().then((data) => {
      if (!cancelled) setWall(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const relay = (anchors || []).filter((anchor) => anchor.kind === "relay");
  const prereg = (anchors || []).filter((anchor) => anchor.kind === "prereg");
  const people = wall?.people || [];
  const hidden = wall?.hidden || 0;
  const peopleTotal = wall ? wall.bound : people.length;
  const total = relay.length + prereg.length + peopleTotal;
  const packetsLeft = relay.reduce((sum, anchor) => sum + (anchor.bless?.left ?? 0), 0);
  const show = (kind: Filter) => filter === "all" || filter === kind;
  const chips: { key: Filter; label: string; count: number }[] = [
    { key: "all", label: "全部", count: total },
    { key: "relay", label: "神明中转站", count: relay.length },
    { key: "prereg", label: "等待认领", count: prereg.length },
    { key: "people", label: "真人", count: peopleTotal },
  ];

  return (
    <section className="fw-wall" aria-labelledby="fw-title">
      <div className="fw-head">
        <h2 id="fw-title">已锚定的脸 <span>{anchors === null ? "…" : total}</span></h2>
        {packetsLeft > 0 && <p className="fw-packets">🧧 今天还剩 {packetsLeft} 个红包 · 扫到神像即领 · 绑脸真人 ×10</p>}
      </div>
      <div className="fw-chips" role="tablist">
        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            role="tab"
            aria-selected={filter === chip.key}
            className={`fw-chip ${filter === chip.key ? "is-on" : ""}`}
            onClick={() => setFilter(chip.key)}
          >
            {chip.label}<i>{chip.count}</i>
          </button>
        ))}
      </div>

      <div className="fw-grid">
        {anchors === null && [0, 1, 2, 3].map((index) => <div key={index} className="fw-card is-loading" />)}

        {show("relay") && relay.map((anchor) => {
          const theme = SHRINE_THEMES[anchor.slug];
          const image = theme?.gallery[0]?.src;
          const claimed = claimedSlugs.includes(anchor.slug);
          const left = anchor.bless?.left ?? 0;
          return (
            <a key={anchor.slug} className="fw-card fw-relay" href={`/${anchor.slug}`}>
              <div className="fw-art">
                {image ? <img src={image} alt={theme?.gallery[0]?.title || anchor.label} loading="lazy" /> : <span className="fw-icon">{theme?.icon || "⚓"}</span>}
                <em className={`fw-badge ${claimed ? "is-done" : ""}`}>{claimed ? "✓ 今日已领" : left > 0 ? `🧧 剩 ${left}` : "明天再来"}</em>
              </div>
              <strong>{theme?.title || anchor.label.split("·")[0].trim()}</strong>
              <small>神明中转站 · {anchor.templates ?? 0} 个形象模板</small>
            </a>
          );
        })}

        {show("prereg") && prereg.map((anchor) => (
          <a key={anchor.slug} className="fw-card fw-prereg" href={`/${anchor.slug}`}>
            <div className="fw-art">
              <span className="fw-mono">{anchor.label.trim().charAt(0).toUpperCase()}</span>
              <em className={`fw-badge ${anchor.claimedBy ? "is-done" : ""}`}>{anchor.claimedBy ? "✓ 已认领" : "等待正主"}</em>
            </div>
            <strong>{anchor.label.split("·")[0].trim()}</strong>
            <small>预注册锚点 · 不存脸纹，凭官方账号认领</small>
          </a>
        ))}

        {show("people") && people.map((person) => (
          <a key={person.name} className="fw-card fw-person" href={`/${person.name}`}>
            <div className={`fw-art ${person.avatar ? "has-avatar" : ""}`}>
              {person.avatar ? <img src={person.avatar} alt={`@${person.name}`} loading="lazy" /> : <NameSigil name={person.name} />}
            </div>
            <strong>@{person.name}</strong>
            <small>真人 · {shortDate(person.since)}上墙</small>
          </a>
        ))}

        {show("people") && wall && (
          <a className="fw-card fw-more" href={myFaceHref}>
            <div className="fw-art"><span className="fw-icon">🙂</span></div>
            <strong>{hidden > 0 ? `还有 ${hidden} 位真人已绑定` : "绑定我的脸"}</strong>
            <small>{hidden > 0 ? "默认不公开 · 本人可选择上墙" : "绑定后可选择是否上墙"}</small>
          </a>
        )}

        {filter === "all" && (
          <a className="fw-card fw-create" href="/create">
            <div className="fw-art"><span className="fw-icon">＋</span></div>
            <strong>创建一个锚点</strong>
            <small>神话、圣像、品牌 · 花 LOVE 铸锚</small>
          </a>
        )}
      </div>

      <p className="fw-privacy">
        墙上的真人只显示名字，以及本人自愿上传的头像（没有就用名字生成的图案）。头像与脸纹分开存放、不参与识别；扫脸时的画面从不上传 ·{" "}
        <a href="https://jic.io/anchor">看看怎么做到的 →</a>
      </p>
    </section>
  );
}
